import { setupListeners } from "@reduxjs/toolkit/query";
import { configureStore } from "@reduxjs/toolkit";

import authSlice from "./auth/auth-slice";
import systemSlice from "./system/system-slice";
import ssoSlice from "./sso/sso-slice";

import { companyUserApi } from "./company/company-user-api";

import companyServiceSlice from "./company/services/service-slices";
import { companyServiceApi } from "./company/services/service-api";

import serviceTxTypeSlice from "./company/services/tx-types/tx-types-slices";
import { serviceTxTypeApi } from "./company/services/tx-types/tx-types-apis";

import companySlice from "./company/company-slice";
import { companyApi } from "./company/company-apis";

import { permissionApi } from "./company/permissions/permission-apis";

import { customerApi } from "./company/services/customers/customer-apis";
import customerSlice from "./company/services/customers/customer-slices";

import { txApis } from "./company/services/transactions/tx-apis";
import txSlice from "./company/services/transactions/tx-slice";

import { transactionTierApi } from "./company/services/tx-tier/tx-apis";

import { serviceFeeGroupApi } from "./company/services/fee/fee-apis";
import feeSlices from "./company/services/fee/fee-slices";

import bankProviderConfigSlice from "./company/services/bank-providers/bank-provider-slices";
import { bankProviderConfigsApi } from "./company/services/bank-providers/bank-provider-api";

import { ssoApi } from "./sso/sso-api";
import { authApi } from "./auth/auth-api";
import { channelApi } from "./system/channel-api";
import { txTypeApi } from "./system/txtype-api";
import { companyInviteApi } from "./company/company-invite-apis";
import { serviceApiConfigApi } from "./company/services/service-api-config-apis";
import { serviceWebhookApi } from "./company/services/service-webhook-apis";
import { companyServiceUserApi } from "./company/services/service-users-apis";
import { reversalApis } from "./company/services/transactions/reversal-apis";
import { productLogApi } from "./company/services/products/log-api";
import { productConfigApi } from "./company/services/products/config-api";
import { productCategoryApi } from "./company/services/products/category-apis";
import { productTransactionApi } from "./company/services/products/tx-logs";
import { settlementReportApi } from "./company/services/products/settlement-report-api";

export const store = configureStore({
  reducer: {
    sso: ssoSlice,
    auth: authSlice,
    system: systemSlice,
    company: companySlice,
    serviceTxType: serviceTxTypeSlice,
    companyService: companyServiceSlice,
    bankProviderConfig: bankProviderConfigSlice,
    customers: customerSlice,
    fee: feeSlices,
    transaction: txSlice,

    [ssoApi.reducerPath]: ssoApi.reducer,
    [authApi.reducerPath]: authApi.reducer,
    [txTypeApi.reducerPath]: txTypeApi.reducer,
    [companyApi.reducerPath]: companyApi.reducer,
    [channelApi.reducerPath]: channelApi.reducer,
    [companyUserApi.reducerPath]: companyUserApi.reducer,
    [serviceTxTypeApi.reducerPath]: serviceTxTypeApi.reducer,
    [companyServiceApi.reducerPath]: companyServiceApi.reducer,
    [bankProviderConfigsApi.reducerPath]: bankProviderConfigsApi.reducer,
    [permissionApi.reducerPath]: permissionApi.reducer,
    [customerApi.reducerPath]: customerApi.reducer,
    [serviceFeeGroupApi.reducerPath]: serviceFeeGroupApi.reducer,
    [txApis.reducerPath]: txApis.reducer,
    [transactionTierApi.reducerPath]: transactionTierApi.reducer,
    [companyInviteApi.reducerPath]: companyInviteApi.reducer,
    [serviceApiConfigApi.reducerPath]: serviceApiConfigApi.reducer,
    [serviceWebhookApi.reducerPath]: serviceWebhookApi.reducer,
    [companyServiceUserApi.reducerPath]: companyServiceUserApi.reducer,
    [reversalApis.reducerPath]: reversalApis.reducer,
    [productLogApi.reducerPath]: productLogApi.reducer,
    [productConfigApi.reducerPath]: productConfigApi.reducer,
    [productCategoryApi.reducerPath]: productCategoryApi.reducer,
    [productTransactionApi.reducerPath]: productTransactionApi.reducer,
    [settlementReportApi.reducerPath]: settlementReportApi.reducer,
  },

  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      ssoApi.middleware,
      authApi.middleware,
      txTypeApi.middleware,
      companyApi.middleware,
      channelApi.middleware,
      companyUserApi.middleware,
      serviceTxTypeApi.middleware,
      companyServiceApi.middleware,
      companyInviteApi.middleware,
      bankProviderConfigsApi.middleware,
      permissionApi.middleware,
      customerApi.middleware,
      serviceFeeGroupApi.middleware,
      txApis.middleware,
      transactionTierApi.middleware,
      serviceApiConfigApi.middleware,
      serviceWebhookApi.middleware,
      companyServiceUserApi.middleware,
      reversalApis.middleware,
      productLogApi.middleware,
      productConfigApi.middleware,
      productCategoryApi.middleware,
      productTransactionApi.middleware,
      settlementReportApi.middleware
    ),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

setupListeners(store.dispatch);
